import stripHtml from '../../utils/stripHtml'

/** Hoeveel tekens er in de tabelcel zelf passen zonder de kolom op te blazen. */
const PREVIEW_LENGTH = 60

/**
 * Hoeveel tekens de tooltip toont. Een notitie kan duizenden tekens lang zijn;
 * een tooltip die het hele scherm vult is niet leesbaarder dan een korte. Wie
 * meer wil zien, opent de notitie zelf.
 */
const TOOLTIP_LENGTH = 600

/**
 * Kort een tekst af en zet er zichtbaar bij dat er meer is.
 *
 * @param {string} text - Platte tekst
 * @param {number} limit - Maximaal aantal tekens
 * @returns {string}
 */
const truncate = (text, limit) => (text.length > limit ? `${text.substring(0, limit)} [...]` : text)

/**
 * Korte weergave van de inhoud van een notitie, voor in een tabelcel.
 *
 * Bewust platte tekst: de inhoud wordt als HTML bewaard, en een HTML-string
 * halverwege afkappen levert een half tag op.
 *
 * @param {string} body - Inhoud die als HTML is opgeslagen (Notes-familie). Platte tekst? Gebruik plainTexts()
 * @returns {string} Afgekapte platte tekst; '' als er geen inhoud is
 */
const notePreview = (body) => truncate(stripHtml(body), PREVIEW_LENGTH)

/**
 * De inhoud van een notitie zoals hij in de tooltip komt te staan, zodat een
 * lange notitie leesbaar is zonder hem te openen.
 *
 * @param {string} body - Inhoud die als HTML is opgeslagen (Notes-familie). Platte tekst? Gebruik plainTexts()
 * @returns {string} Platte tekst; '' als er geen inhoud is
 */
const noteTooltipText = (body) => truncate(stripHtml(body), TOOLTIP_LENGTH)

/**
 * Beide weergaven in één keer, voor wie ze allebei nodig heeft.
 *
 * De losse functies strippen elk de hele body; een tabelcel die ze allebei
 * aanroept doet dat werk dus twee keer, bij elke render van de tabel — en die
 * rendert opnieuw bij elke toetsaanslag in het zoekveld. Hier wordt één keer
 * gestript en twee keer afgekapt.
 *
 * @param {string} body - Inhoud die als HTML is opgeslagen (Notes-familie). Platte tekst? Gebruik plainTexts()
 * @returns {{preview: string, full: string}} De cel-weergave en de tooltip-tekst
 */
const noteTexts = (body) => {
    const plain = stripHtml(body)

    return {
        preview: truncate(plain, PREVIEW_LENGTH),
        full: truncate(plain, TOOLTIP_LENGTH),
    }
}

/**
 * Breedte waarop een notitie-tooltip mag uitlopen. Hier, niet in elke cel apart:
 * twee kopieën met een comment erbij ("zelfde maat als …") is geen waarborg maar
 * de aankondiging dat ze gaan afwijken, en beide kanten hebben groene tests.
 * AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
 */
const TOOLTIP_WIDTH = '420px'

/**
 * Zelfde afkapping als `noteTexts`, maar voor inhoud die al PLATTE TEKST is.
 *
 * `noteTexts` haalt er eerst de HTML uit, want de Notes-collectie bewaart HTML.
 * Op platte tekst is die stap niet neutraal maar schadelijk: `<[^>]*>` vreet een
 * stuk uit een notitie als `budget <500> per maand`, en dan staat er in de rij
 * iets anders dan in de database. Gebruik deze variant voor velden die als
 * platte tekst worden opgeslagen, zoals `SubscriptionNotes.Note`.
 *
 * Witruimte wordt wél samengetrokken: de cel is een samenvatting van één regel.
 * De volledige tekst met regelovergangen staat in het bewerkveld.
 *
 * @param {string} text - Platte tekst
 * @returns {{preview: string, full: string}} De cel-weergave en de tooltip-tekst
 */
const plainTexts = (text) => {
    const plain = String(text || '').replace(/\s+/g, ' ').trim()

    return {
        preview: truncate(plain, PREVIEW_LENGTH),
        full: truncate(plain, TOOLTIP_LENGTH),
    }
}

export { notePreview, noteTooltipText, noteTexts, plainTexts, PREVIEW_LENGTH, TOOLTIP_LENGTH, TOOLTIP_WIDTH }
export default notePreview
